import React, { useState, useRef, useEffect } from "react";
import { Modal } from "antd";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import ReactMapboxGl, { Marker } from "react-mapbox-gl";
import { setMapboxRTLTextPlugin } from "helper/util";
import "./FailedStopMap.scss";

mapboxgl.workerClass =
  require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;
setMapboxRTLTextPlugin(mapboxgl);

const Map = ReactMapboxGl({
  minZoom: 8,
  accessToken: window.REACT_APP_MAPBOX_KEY,
  onStyleLoad: (map) => map.resize()
});

const FailedStopMap = ({ driverLocation, dropoffLocation, locationType }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const mapRefModal = useRef(null);
  const mapBoundsRef = useRef(null);

  // Calculate bounds before early return
  const hasValidLocations =
    driverLocation?.lat &&
    driverLocation?.lng &&
    dropoffLocation?.lat &&
    dropoffLocation?.lng;

  const getBounds = () => {
    if (!hasValidLocations) return null;

    const lats = [Number(driverLocation.lat), Number(dropoffLocation.lat)];
    const lngs = [Number(driverLocation.lng), Number(dropoffLocation.lng)];

    const x0 = Math.min(...lats);
    const x1 = Math.max(...lats);
    const y0 = Math.min(...lngs);
    const y1 = Math.max(...lngs);

    const latCenter = (x1 + x0) / 2;
    const lngCenter = (y1 + y0) / 2;

    return {
      bounds: [
        [y1, x1],
        [y0, x0]
      ],
      center: {
        lat: latCenter,
        lng: lngCenter
      }
    };
  };

  const mapData = getBounds();
  if (mapData?.bounds) {
    mapBoundsRef.current = mapData.bounds;
  }

  useEffect(() => {
    if (isModalVisible && mapRefModal.current && mapBoundsRef.current) {
      // Trigger map resize after modal animation completes
      const timer = setTimeout(() => {
        if (mapRefModal.current && mapBoundsRef.current) {
          mapRefModal.current.resize();
          // Force fitBounds again after resize
          mapRefModal.current.fitBounds(mapBoundsRef.current, { padding: 50 });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isModalVisible]);

  if (!hasValidLocations || !mapData) {
    return null;
  }

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const renderMarkers = () => (
    <>
      {/* Driver GPS location at time of fail */}
      <Marker
        coordinates={[Number(driverLocation.lng), Number(driverLocation.lat)]}
        anchor="bottom"
      >
        <div className="failed-map-marker driver-location">
          <div className="marker-icon">🚗</div>
          <div className="marker-label">Driver Location</div>
        </div>
      </Marker>

      {/* Drop-off location at time of fail */}
      <Marker
        coordinates={[Number(dropoffLocation.lng), Number(dropoffLocation.lat)]}
        anchor="bottom"
      >
        <div className="failed-map-marker dropoff-location">
          <div className="marker-icon">📍</div>
          <div className="marker-label">
            {locationType === "pickup" ? "Pickup" : "Drop-off"} Location
          </div>
        </div>
      </Marker>
    </>
  );

  const renderMap = (
    height,
    width,
    interactive = true,
    padding = 40,
    isModal = false
  ) => (
    <Map
      center={[mapData.center.lng, mapData.center.lat]}
      fitBounds={mapData.bounds}
      fitBoundsOptions={{ padding }}
      style="mapbox://styles/mapbox/streets-v11"
      movingMethod="easeTo"
      containerStyle={{
        height,
        width
      }}
      interactive={interactive}
      onStyleLoad={(map) => {
        if (isModal) {
          mapRefModal.current = map;
          // Additional resize after style loads
          setTimeout(() => {
            map.resize();
            if (mapData?.bounds) {
              map.fitBounds(mapData.bounds, { padding });
            }
          }, 100);
        }
      }}
    >
      {renderMarkers()}
    </Map>
  );

  return (
    <div className="failed-stop-map-container">
      <div className="failed-stop-map-thumbnail" onClick={showModal}>
        {renderMap(150, "100%", false, 60)}
        <div className="thumbnail-overlay">
          <span>Click to expand</span>
        </div>
      </div>

      <Modal
        title="Failed Stop Location Details"
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={900}
        bodyStyle={{ padding: 0 }}
        className="failed-stop-map-modal"
      >
        <div className="modal-map-wrapper">{renderMap(600, "100%", true, 50, true)}</div>
      </Modal>
    </div>
  );
};

export default FailedStopMap;
